/**
 * UI switches — code constants, flipped by editing this file (no env var, no
 * rebuild flag). Read them with a strict `=== true`, so a switch that is set
 * to anything else, or removed from this file, means OFF.
 */

/**
 * USE_WEB_APP_CALENDAR — which calendar EVERY date field in both portals
 * opens (docs/knowledge/calendars.md).
 *
 *   true            the traveller app's calendar (components/calendar —
 *                   react-calendar, brand discs, tinted range band, live hover
 *                   preview). Each field keeps its own input box; only the
 *                   popup changes. A start / end pair (From–To, Check-in /
 *                   Check-out, Sales / Travel / Blackout Start–End …) opens
 *                   ONE range calendar from either box; a single date opens
 *                   a single-date one. The hotel Modify modal keeps its
 *                   two-half card (HotelDateRangePicker).
 *   false / absent  the AntD calendar everywhere, under the same boxes
 *                   (components/fields/AntDateTextField, AntDateField): value
 *                   shown "10 Oct 26"; a start / end pair is two linked AntD
 *                   calendars (range paint, picking the start opens the end);
 *                   kept inside a modal;
 *                   date + time is one AntD box with time columns; the
 *                   partner reports keep AntD's RangePicker; the Modify
 *                   modal shows StayDateRangeFields.
 *
 * Either way every calendar is the compact one of the hotel Modify modal,
 * and a start / end pair hands over like the manual hotel form: picking the
 * start opens the end's calendar under the End box when the end is empty.
 * Time pickers are not affected either way. Both follow the portal's
 * branding colour for the selected days and hover.
 */
export const USE_WEB_APP_CALENDAR = true;

/**
 * WEB_APP_CALENDAR_EXPAND — how the web-app calendar (USE_WEB_APP_CALENDAR)
 * opens in the hotel Modify modal.
 *
 *   true            inside that hotel's card, under the dates: the card grows
 *                   smoothly to fit the calendar and shrinks back when it
 *                   closes (slide / expand animation)
 *   false / absent  a floating calendar over the card, compact, opening
 *                   upward inside the modal
 *
 * Applies to the web-app calendar only — the AntD calendar always floats.
 */
export const WEB_APP_CALENDAR_EXPAND = false;

/**
 * SHOW_API_STATUS_BADGE — the QA-only status dot on sidebar nav items in both
 * portals (constants/apiStatusBadge.js; each item's `apiStatus` in the two
 * navigation configs picks the colour).
 *
 *   true            green dot = the screen's APIs are QA-verified, yellow = in
 *                   progress, on every nav item that carries an `apiStatus`
 *   false / absent  no dots: nav items show icon + label only
 */
export const SHOW_API_STATUS_BADGE = false;

/**
 * FLIGHT_MODIFY_AUTOMATE — Manual vs Automate in the flight Modify modal
 * (SMB-4379). Stands in for the backend's automate flag until it exists.
 *
 *   true            the modal offers Automate: search the supplier, pick one
 *                   of the returned flights, check availability + price, quote.
 *                   Search and check use SAMPLE data until the APIs land
 *                   (Api/flightModifyAutomateAPI.js).
 *   false / absent  the modal offers Enter Details Manually.
 */
export const FLIGHT_MODIFY_AUTOMATE = true;

/**
 * USE_MOCK_MODIFY_REQUESTS — where hotel MANUAL modification requests go
 * (Modify modal → "Manual request", the Request tab's requests and quotes).
 *
 *   true            a local mock (Api/hotelModifyRequestMock.js, kept in
 *                   sessionStorage per booking) that plays the backend: a
 *                   request turns into a quote or completes a few seconds
 *                   after it is sent, so the whole flow can be tried
 *   false / absent  the real endpoints, trips/hotels/modify-request/*
 *                   (APImdFiles/HOTEL_MODIFY_REQUEST_API.md) — set this once
 *                   app-backend ships them
 */
export const USE_MOCK_MODIFY_REQUESTS = true;

/**
 * USE_MOCK_FLIGHT_MODIFY_REQUESTS — where FLIGHT manual modification requests
 * go (Modify modal → "Manual request", the Request tab's flight requests and
 * quotes). A separate flow from the hotel one (USE_MOCK_MODIFY_REQUESTS above).
 *
 *   true            a local mock (Api/flightModifyRequestMock.js, kept in
 *                   localStorage per booking) that plays the backend
 *   false / absent  the real endpoints, flight/modify-request/*
 *                   (APImdFiles/FLIGHT_MODIFY_REQUEST_API.md) — set this once
 *                   app-backend ships them
 */
export const USE_MOCK_FLIGHT_MODIFY_REQUESTS = true;

/**
 * USE_MOCK_ACTIVITY_MODIFY_REQUESTS — where ACTIVITY manual requests go
 * (activity Modify / Cancel → "Manual request", the Request tab's activity
 * requests and quotes).
 *
 *   true            the hotel mock's activity stores (Api/hotelModifyRequestMock.js,
 *                   localStorage per booking — admin and partner tabs share it)
 *   false / absent  the real endpoints, trips/activities/{modify,cancel}-request/*
 *                   (APImdFiles/ACTIVITY_MANUAL_REQUEST_API.md) — set this once
 *                   app-backend ships them
 */
export const USE_MOCK_ACTIVITY_MODIFY_REQUESTS = true;

/**
 * HIDE_CANCELLED_HOTELS_WITHOUT_USER_IN_BILLING — the booking detail Billing
 * tab (both portals) and the hotel rows /get sends as cancelled with no
 * `cancelledBy` (no user is recorded as cancelling them — e.g. the stay a
 * Modify replaced; helpers/hotelRowVisibility.js).
 *
 *   true            Billing leaves their fare rows out, as the Itinerary does
 *   false / absent  Billing lists them; the Itinerary (and the print) hides
 *                   them either way
 *
 * The Billing summary (Total Amount, Markup, …) stays what /get sends either
 * way — it still counts those rows.
 */
export const HIDE_CANCELLED_HOTELS_WITHOUT_USER_IN_BILLING = false;

/**
 * QA_LOGIN_AUTOFILL — fill the seeded QA logins on both login screens
 * (helpers/qaLoginAutofill.js).
 *
 *   true            Partner: partner code 4732178 → email + password, and the
 *                   OTP step opens with the QA code filled in. CRM:
 *                   qa-admin@zenvoya.ai → password, and the same on the OTP
 *                   step. Fill only — Sign In / Verify are still clicked.
 *   false / absent  plain empty login screens; the credentials are compiled out
 *                   of the bundle.
 *
 * ⚠️ Unlike an env flag this is the SAME in every build: while it is true the QA
 * passwords ship to every environment the bundle is deployed to, production
 * included. Set it to false before a release / production merge.
 */
export const QA_LOGIN_AUTOFILL = true;
